import { Loader, apiDateFormat } from "@chhjit/react-components";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@material-ui/core";
import moment from "moment";
import { Suspense, memo, useCallback, useEffect, useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { useEmptyRequiredFields } from "../../../entities/booking-process";
import { useServiceSteps, wizardApi } from "../../../entities/wizard";
import { BlockTextProvider } from "../../context/step-config-context";
import { useDebouncedHookFormValues } from "../../hooks";
import type {
  ComponentContentBlock,
  StepConfig,
} from "../../types/workflow-schema";
import {
  expandFieldNamesForForm,
  expandRequiredFieldLabels,
  getFieldsFromConfig,
} from "../../types/workflow-schema";
import type { ContractExecutors } from "./api-contracts";
import { componentRegistry } from "./component-registry";
import { ContentBlockRenderer, FieldBlockRenderer } from "./field-renderers";
import { PreSubmitValidationProvider } from "./pre-submit-validation";
import { buildYupSchema } from "./schema-to-yup";
import { useFieldBindings } from "./use-field-bindings";
import { useStepConfig } from "./use-step-config";
import { useStepSubmit } from "./use-step-submit";

type DynamicStepRendererProps = {
  stepId: string;
  /** Contract executors for API orchestration on submit */
  executors?: ContractExecutors;
};

/**
 * Renders a booking step dynamically based on config from Sanity.
 *
 * Resolution order:
 * 1. If contentBlocks has a "component" block → look up in component registry
 * 2. If contentBlocks has field blocks → render SchemaFormRenderer
 * 3. Fallback → error state
 */
export const DynamicStepRenderer = memo(
  ({ stepId, executors }: DynamicStepRendererProps) => {
    const { stepConfig, isLoading, isStepConfigReady } = useStepConfig(stepId);

    if (isLoading || !isStepConfigReady || !stepConfig) {
      return <Loader size="large" />;
    }

    // Check for a component block in contentBlocks
    const componentBlock = stepConfig.contentBlocks?.find(
      (b): b is ComponentContentBlock => b.blockType === "component",
    );
    const hasFieldBlocks = stepConfig.contentBlocks?.some(
      (b) => b.blockType === "field",
    );

    // Fast path: component-ONLY step (no field blocks alongside it).
    // The component owns its own form and submit handler.
    // If field blocks are also present, fall through to SchemaFormRenderer
    // which renders the component inline alongside the fields.
    if (componentBlock && !hasFieldBlocks) {
      const Component = componentRegistry[componentBlock.componentKey];
      if (Component) {
        // Extract non-field, non-component content blocks to render alongside
        const extraBlocks = stepConfig.contentBlocks?.filter(
          (b) => b.blockType !== "field" && b.blockType !== "component",
        );

        return (
          <Suspense fallback={<Loader size="large" />}>
            <BlockTextProvider textBlocks={componentBlock.textBlocks}>
              <Component />
            </BlockTextProvider>
            {extraBlocks && extraBlocks.length > 0 && (
              <Grid container spacing={2}>
                {extraBlocks.map((block, i) => (
                  <ContentBlockRenderer
                    key={(block as any).key ?? i}
                    block={block}
                  />
                ))}
              </Grid>
            )}
          </Suspense>
        );
      }
    }

    // Schema-driven field rendering
    const hasContent =
      (stepConfig.contentBlocks && stepConfig.contentBlocks.length > 0) ||
      (stepConfig.fields && stepConfig.fields.length > 0);

    if (hasContent && executors) {
      // Provider sits above SchemaFormRenderer so the renderer's useStepSubmit
      // (runs validators) and descendant field renderers (register them) share
      // one instance.
      return (
        <PreSubmitValidationProvider>
          <SchemaFormRenderer
            key={stepId}
            stepId={stepId}
            stepConfig={stepConfig}
            executors={executors}
          />
        </PreSubmitValidationProvider>
      );
    }

    // Fallback — component not found
    return (
      <div>
        <p>Step configuration error: No component found for "{stepId}"</p>
      </div>
    );
  },
);

/**
 * Schema-driven form renderer.
 *
 * Renders fields from CMS step config using the field renderer registry.
 * Supports two modes:
 * - `contentBlocks[]` — ordered mix of fields, text, dividers, notes
 * - `fields[]` — just fields in order (backward compatible)
 */
const SchemaFormRenderer = memo(
  ({
    stepId,
    stepConfig,
    executors,
  }: {
    stepId: string;
    stepConfig: StepConfig;
    executors: ContractExecutors;
  }) => {
    const fields = useMemo(() => getFieldsFromConfig(stepConfig), [stepConfig]);

    const { syncToStores, readFromStores } = useFieldBindings(stepId);
    const { category, current } = useServiceSteps();

    // Expand composite field names (e.g. address → address, address2, city, state, postal, country)
    const expandedFieldNames = useMemo(
      () => expandFieldNamesForForm(fields),
      [fields],
    );

    // Build Yup schema from field definitions
    const validationSchema = useMemo(() => buildYupSchema(fields), [fields]);

    // Read initial values from stores (using expanded names for composite fields).
    // Apply field-level defaultValue for fields that have no persisted store value.
    // For select fields with schema options: convert plain strings returned by readTransform
    // to { value, label } objects that react-select (FormNewSelect) requires to display
    // selected options. Fields with dynamic options (floor, accessType) are handled inside
    // DwellingDetailsBlock via a mount-only effect instead.
    const defaultValues = useMemo(() => {
      const fromStores = readFromStores(expandedFieldNames);
      for (const field of fields) {
        // Convert stored plain strings → option objects for select fields with schema options
        if (
          field.type === "select" &&
          Array.isArray(field.options) &&
          field.options.length > 0
        ) {
          const storedVal = fromStores[field.name];
          if (typeof storedVal === "string" && storedVal !== "") {
            const opt = field.options.find((o) => o.value === storedVal);
            if (opt)
              fromStores[field.name] = { value: opt.value, label: opt.label };
          }
        }
        // Calendar fields default to tomorrow so the form is valid on first render
        // (avoids a timing gap where the CalendarFieldRenderer's useEffect fires
        // after mount and isValid is briefly false, showing the required-fields tooltip).
        if (field.type === "calendar" && fromStores[field.name] == null) {
          fromStores[field.name] = moment().add(1, "day").format(apiDateFormat);
        }
        // Apply field-level defaultValue when no stored value
        if (field.defaultValue != null && fromStores[field.name] == null) {
          if (
            field.type === "select" &&
            Array.isArray(field.options) &&
            field.options.length > 0
          ) {
            // For select fields: defaultValue is a plain string — convert to option object
            const opt = field.options.find(
              (o) => o.value === String(field.defaultValue),
            );
            fromStores[field.name] = opt
              ? { value: opt.value, label: opt.label }
              : field.defaultValue;
          } else {
            fromStores[field.name] = field.defaultValue;
          }
        }
        // Coerce increment/number defaults to numbers so the IncrementInput
        // `+` button doesn't do string concatenation (e.g. "0" + 1 = "01").
        if (
          (field.type === "increment" || field.type === "number") &&
          fromStores[field.name] != null
        ) {
          fromStores[field.name] = Number(fromStores[field.name]) || 0;
        }
        // Hidden fields are signals — placing one on a step means "fire this
        // action on submit" — so their form value never comes from the store.
        // A one-shot gate (bookingConfirmed, disarmed to false by
        // branching-rules right after the contracts consume it) would
        // otherwise be read back as `false` when the user navigates BACK to
        // the step, and the resubmit could never re-fire quoteCalculate — the
        // flow would keep branching on a stale calculation. CMS defaultValue
        // still wins when set; the editor shouldn't need an explicit
        // defaultValue: true.
        if (field.type === "hidden") {
          fromStores[field.name] = field.defaultValue ?? true;
        }
      }
      return fromStores;
    }, [expandedFieldNames, readFromStores, fields]);

    const formMethods = useForm({
      defaultValues,
      resolver: yupResolver(validationSchema),
      mode: "all",
    });

    const {
      control,
      clearErrors,
      formState: { isSubmitting },
      handleSubmit,
      trigger,
    } = formMethods;

    // Watch all form values so we can re-validate whenever anything changes.
    // We deliberately do NOT rely on formState.isValid here because clearErrors()
    // (called in the mount effect below) can corrupt isValid by temporarily
    // clearing all errors, making the form appear valid even when it isn't.
    const formValues = useWatch({ control });

    // Build field label map for empty field tracking (expanded for composite fields)
    const fieldLabels = useMemo(
      () => expandRequiredFieldLabels(fields),
      [fields],
    );

    useEmptyRequiredFields(control, fieldLabels, {
      trigger,
      validationSchema,
    });

    // Track whether the initial mount trigger() has completed.
    // We must not call wizardApi.setValid(false) before trigger() runs —
    // doing so overwrites any persisted valid:true state from a prior visit
    // to this step (e.g. navigating back), leaving the Next button permanently disabled.
    const initialValidationDoneRef = useRef(false);

    // Track whether the user has attempted to submit this step.
    // Before submit: always clear errors after trigger() so fields never turn red
    // until the user explicitly presses Next. After a failed submit: let errors
    // remain so the user can see what needs to be fixed.
    const hasAttemptedSubmitRef = useRef(false);

    // Trigger validation on mount so pre-filled values activate the Next button.
    // We also fire setValid here (after await) so the wizard state is correct
    // from the very first render pass.
    useEffect(() => {
      hasAttemptedSubmitRef.current = false;
      trigger().then((isFormValid) => {
        // flushSync batches clearErrors + flag into one render cycle,
        // preventing the intermediate render where trigger()-set errors are briefly visible.
        flushSync(() => {
          clearErrors();
          initialValidationDoneRef.current = true;
        });
        wizardApi.setValid({
          service: category?.slug,
          step: current?.key,
          valid: isFormValid,
        });
      });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync form changes to stores (debounced).
    // Hidden fields are excluded — they only sync on explicit submit to prevent
    // premature contract evaluation (e.g. bookingConfirmed firing appointmentCreate
    // if the user visits the confirmation step then navigates back).
    const handleDebouncedChange = useCallback(
      (data: Record<string, unknown>) => {
        const filteredData = Object.fromEntries(
          Object.entries(data).filter(([key]) => {
            const field = fields.find((f) => f.name === key);
            return !field || field.type !== "hidden";
          }),
        );
        syncToStores(filteredData);
      },
      [syncToStores, fields],
    );

    useDebouncedHookFormValues(control, handleDebouncedChange);

    // Sync validity to wizard whenever form values change (after initial validation).
    // We call trigger() directly rather than reading formState.isValid, because
    // clearErrors() in the mount effect can temporarily zero out errors and make
    // isValid appear true even when required fields are empty.
    //
    // Before the user attempts to submit: always clear errors after trigger() so
    // fields don't turn red while the user is still filling in the form.
    // After a failed submit attempt: keep errors visible so the user sees what to fix.
    useEffect(() => {
      if (!initialValidationDoneRef.current) return;
      trigger().then((isFormValid) => {
        if (!hasAttemptedSubmitRef.current) {
          clearErrors();
        }
        wizardApi.setValid({
          service: category?.slug,
          step: current?.key,
          valid: isFormValid,
        });
      });
    }, [category?.slug, current?.key, formValues]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      wizardApi.setSubmitting({
        service: category?.slug,
        step: current?.key,
        submitting: isSubmitting,
      });
    }, [category?.slug, current?.key, isSubmitting]);

    // Submit handler: sync final data then run API contract orchestrator
    const onStepSubmit = useStepSubmit(stepId, { executors });

    return (
      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit(onStepSubmit, () => {
            // Failed submit: mark that the user has tried so errors stay visible.
            hasAttemptedSubmitRef.current = true;
          })}
          id="ob-page-form"
        >
          <Grid container spacing={2}>
            {stepConfig.contentBlocks
              ? stepConfig.contentBlocks.map((block, i) => {
                  // Component blocks render inline within the SchemaFormRenderer form.
                  // Used for display-only components (e.g. pricing-list) that sit
                  // alongside form fields without owning their own form or submit handler.
                  if (block.blockType === "component") {
                    const Component =
                      componentRegistry[
                        (block as ComponentContentBlock).componentKey
                      ];
                    if (!Component) return null;
                    return (
                      <Grid item xs={12} key={(block as any).key ?? i}>
                        <Suspense fallback={<Loader size="large" />}>
                          <BlockTextProvider
                            textBlocks={
                              (block as ComponentContentBlock).textBlocks
                            }
                          >
                            <Component />
                          </BlockTextProvider>
                        </Suspense>
                      </Grid>
                    );
                  }
                  return (
                    <ContentBlockRenderer
                      key={
                        block.blockType === "field"
                          ? block.name
                          : ((block as any).key ?? i)
                      }
                      block={block}
                      control={control}
                    />
                  );
                })
              : fields.map((field) => (
                  <FieldBlockRenderer
                    key={field.name}
                    field={field}
                    control={control}
                  />
                ))}
          </Grid>
        </form>
      </FormProvider>
    );
  },
);
